import { useState } from 'react';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../../config/firebase';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { sanitizeReturnTo } from '../../utils/returnTo';
import logger from '../../utils/logger';

import styles from './auth.module.scss';

const isBenignAuthPopupError = (error) => {
  const msg = String(error?.message || '');
  return (
    error?.code === 'auth/popup-closed-by-user' ||
    msg.includes('Pending promise was never set')
  );
};

const AuthContainer = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState('');
  const [disabled, setDisabled] = useState(false);
  const db = getFirestore();

  const returnTo = sanitizeReturnTo(
    new URLSearchParams(location.search).get('returnTo')
  );

  const finishSignIn = async (user) => {
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      await setDoc(userDocRef, {
        email: user.email,
        displayName: user.displayName,
        avatar: user.photoURL || '../avatar.png',
        isNewUser: true,
      });
      navigate(
        returnTo ? `/settings?returnTo=${encodeURIComponent(returnTo)}` : '/settings'
      );
      return;
    }

    const stayOnCurrentPage = `${location.pathname}${location.search}`;
    navigate(returnTo || stayOnCurrentPage || '/');
  };

  const signInWithGoogle = async () => {
    setDisabled(true);
    setErrorMessage('');
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
    });

    try {
      const result = await signInWithPopup(auth, provider);
      await finishSignIn(result.user);
    } catch (error) {
      // signInWithPopup can throw this after the popup closes even when auth
      // succeeded — onAuthStateChanged fires first. Recover when we have a user.
      if (isBenignAuthPopupError(error) && auth.currentUser) {
        logger.warn(
          'Auth popup teardown race after successful sign-in; continuing',
          error
        );
        try {
          await finishSignIn(auth.currentUser);
          return;
        } catch (finishError) {
          logger.error('Error finishing sign-in after popup race:', finishError);
          setErrorMessage(`${finishError.code || 'error'}: ${finishError.message}`);
          return;
        }
      }

      if (error?.code === 'auth/popup-closed-by-user') {
        return;
      }

      setErrorMessage(`${error.code}: ${error.message}`);
    } finally {
      setDisabled(false);
    }
  };

  return (
    <>
      <button
        className={styles.authButton}
        onClick={signInWithGoogle}
        disabled={disabled}
      >
        <span className={styles.ninetyDegrees}>Login / Sign up</span>
      </button>
      {errorMessage}
    </>
  );
};

export default AuthContainer;
